import { memo, useEffect, useRef, useState } from "react";
import "./style.scss";
import { useOutsideClick } from "../../../hooks/useOutsideClick";
import classNames from "classnames";
import OliverText from "@builder/components/general/OliverText";
import CheckboxOptions from "./SelectItems/CheckboxOptions";
import { useConditionData } from "@builder/hooks/useConditionsData";
import InfiniteScroll from "../InfiniteScroll";
import SelectDisplay from "./SelectDisplay";
import SelectItems from "./SelectItems/SelectItems";
import useDebounce from "@builder/hooks/useDebounce";

interface SelectInputProps {
    title?: string;
    value?: string;
    layout?: "row" | "column";
    className?: string;
    options: string[];
    callback: (value: string) => void;
    searchable?: boolean;
    isMulti?: boolean;
    placeholder?: string;
    source?: string;
    action?: boolean;
    error?: boolean;
}

const SelectInput: React.FC<SelectInputProps> = memo(
    ({
        title,
        value = "",
        layout = "row",
        className,
        options,
        callback,
        searchable,
        isMulti = false,
        placeholder,
        action = false,
        source,
        error = false
    }) => {
        const [isOpen, setIsOpen] = useState<boolean>(false);
        const [isActionOpen, setActionOpen] = useState<boolean>(false);
        const selectRef = useRef<HTMLInputElement>(null);
        const [searchValue, setSearchValue] = useState<string>("");
        const debouncedValue = useDebounce(searchValue, 400);
        const [isSearching, setSearching] = useState<boolean>(false);
        const [checkedOptions, setCheckedOptions] = useState<Record<string, boolean>>({});
        const [isLoading, setIsLoading] = useState(false);
        const { searchOptions, getItemById, getAPITargetOptions, setHasMore, setOffset, offset, hasMore } = useConditionData();
        const [filteredOptions, setFilteredOptions] = useState<string[] | null>(options);

        const selectWrapperClass = classNames("select-input-wrapper-con", {
            ["layout-row"]: layout === "row",
            ["layout-column"]: layout === "column",
            [`${className}`]: className
        });

        useOutsideClick(selectRef, () => {
            setIsOpen(false);
            setActionOpen(false);
        });

        const handleIntersect = async () => {
            if (isLoading || searchValue) return;

            setIsLoading(true);
            const d = await getAPITargetOptions(source || "", offset);
            if (!d) {
                setIsLoading(false);
                return;
            }

            const newOffset = offset + Object.keys(d.hits).length;
            const hasMoreItems = newOffset < d.total;

            setOffset(newOffset);
            setHasMore(hasMoreItems);
            setIsLoading(false);
        };

        useEffect(() => {
            if (isMulti && value === "") setCheckedOptions({});
        }, [value, isMulti]);

        useEffect(() => {
            if (isMulti) setCheckedOptions({});
        }, [source]);

        useEffect(() => {
            if (isMulti && Object.keys(checkedOptions).length > 0) {
                callback(Object.keys(checkedOptions).join(", "));
                setSearching(false);
                setSearchValue("");
            }
        }, [checkedOptions, isMulti]);

        useEffect(() => {
            const getItemNames = async (value: string) => {
                if (!source) return;

                const str = await getItemById(source || "", value);
                const names = str?.split(", ");
                const val: Record<string, boolean> = {};
                names?.forEach(name => (val[name] = true));
                setCheckedOptions(val);
            };
            if (isMulti && value) {
                getItemNames(value);
            }
        }, []);

        useEffect(() => {
            const updateSearchResults = async () => {
                if (debouncedValue !== "") {
                    try {
                        const results = await searchOptions(source!, debouncedValue);
                        setFilteredOptions(results);
                    } catch (error) {
                        console.error(error);
                        setFilteredOptions(null);
                    }
                } else {
                    setFilteredOptions(options);
                }
            };

            updateSearchResults();
        }, [debouncedValue, searchOptions, source, options]);

        useEffect(() => {
            setOffset(20);
            setHasMore(true);
        }, [source, setOffset, setHasMore]);

        return (
            <div className={selectWrapperClass}>
                {title && <OliverText text={title} size="xs" />}

                <div className={["select-wrapper-con", isOpen && "select-active-c"].filter(Boolean).join(" ")} ref={selectRef}>
                    <img src="/angled-arrow-down.svg" id="dropdownIcon" />
                    <SelectDisplay
                        placeholder={placeholder || ""}
                        searchable={searchable || false}
                        isSearching={isSearching}
                        action={action}
                        searchValue={searchValue}
                        isMulti={isMulti}
                        checkedOptions={checkedOptions}
                        value={value}
                        setIsOpen={setIsOpen}
                        setSearching={setSearching}
                        setSearchValue={setSearchValue}
                        error={error}
                    />

                    {isOpen &&
                        (isMulti ? (
                            <CheckboxOptions
                                hasMore={hasMore}
                                handleIntersect={handleIntersect}
                                options={filteredOptions || []}
                                checkedOptions={checkedOptions}
                                setCheckedOptions={setCheckedOptions}
                                isLoading={isLoading}
                            />
                        ) : (
                            <InfiniteScroll hasMore={hasMore} onIntersect={handleIntersect}>
                                <SelectItems
                                    filteredOptions={filteredOptions || []}
                                    callback={callback}
                                    isMulti={isMulti}
                                    setIsOpen={setIsOpen}
                                    setSearching={setSearching}
                                    isActionOpen={isActionOpen}
                                    setActionOpen={setActionOpen}
                                    isLoading={isLoading}
                                />
                            </InfiniteScroll>
                        ))}
                </div>
            </div>
        );
    }
);

export default SelectInput;
